import { useEffect, useState } from 'react';
import { API_URL, MARKETING_URL } from '../../lib/env';

interface BannerData {
    active: boolean;
    message: string;
    level: 'info' | 'warning' | 'success' | 'error';
    cta_label: string | null;
    cta_url: string | null;
}

const LEVEL_STYLES: Record<string, string> = {
    info: 'bg-blue-600    text-white dark:bg-blue-700    dark:text-blue-50',
    warning: 'bg-amber-500   text-white dark:bg-amber-600   dark:text-white',
    success: 'bg-emerald-600 text-white dark:bg-emerald-700 dark:text-emerald-50',
    error: 'bg-red-600     text-white dark:bg-red-700     dark:text-red-50',
};

const DEFAULT_CTA_LABEL = 'Learn more';

function defaultChangelogUrl(): string {
    const base = MARKETING_URL?.replace(/\/$/, '');
    return base ? `${base}/changelog` : '/changelog';
}

function resolveCta(banner: BannerData): { label: string; url: string } {
    return {
        label: banner.cta_label?.trim() || DEFAULT_CTA_LABEL,
        url: banner.cta_url?.trim() || defaultChangelogUrl(),
    };
}

function isBannerSuppressed(): boolean {
    if (typeof window === 'undefined') return false;
    if (window.self !== window.top) return true;
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'hero';
}

export function ReleaseBanner() {
    const [banner, setBanner] = useState<BannerData | null>(null);
    const [suppressed] = useState(isBannerSuppressed);

    const fetch_banner = async () => {
        try {
            const res = await fetch(`${API_URL}/v1/release-banner`);
            if (!res.ok) return;
            const data: BannerData | null = await res.json();
            if (data?.active) {
                setBanner(data);
            } else {
                setBanner(null);
            }
        } catch {
            // silently ignore network errors
        }
    };

    useEffect(() => {
        fetch_banner();
    }, []);

    if (suppressed || !banner) return null;

    const styles = LEVEL_STYLES[banner.level] ?? LEVEL_STYLES.info;
    const cta = resolveCta(banner);

    return (
        <div
            data-testid="release-banner"
            className={`px-4 py-2 text-sm font-medium shadow-md ${styles}`}
        >
            <p className="text-center leading-snug">
                {banner.message}{' '}
                <a
                    href={cta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline underline underline-offset-2 opacity-90 hover:opacity-100 font-semibold whitespace-nowrap"
                >
                    {cta.label}
                </a>
            </p>
        </div>
    );
}
